import { useCallback, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { PushProvider, PushToast, pushLinkTarget, usePushRegistration } from '@kilat/ui';

import { useAuth } from '../use-auth.js';
import { useI18n } from '../use-i18n.js';
import { dashboardPushClient } from './client.js';

/** Below the sticky AppShellBar: one touch-target row plus its vertical padding. */
const TOAST_TOP = 'calc(var(--touch-target) + var(--space-2) * 2 + var(--space-3))';

/** Follow a push deep link in-app when it is ours, else load it. */
function useFollowPushLink(): (link: string) => void {
  const nav = useNavigate();
  return useCallback(
    (link: string) => {
      const target = pushLinkTarget(link, {
        base: '/',
        origin: window.location.origin,
        routeRoots: null,
      });
      if (target.routerPath) nav(target.routerPath);
      else window.location.assign(target.href);
    },
    [nav],
  );
}

/** One push provider for the dashboard, inside the router so taps route in-app. */
export function DashboardPushProvider({ children }: { readonly children: ReactNode }) {
  const { user } = useAuth();
  const follow = useFollowPushLink();
  return (
    <PushProvider client={dashboardPushClient} uid={user?.uid ?? null} onNavigate={follow}>
      {children}
    </PushProvider>
  );
}

/**
 * Foreground push → a banner on any screen. Stacks just below the sticky
 * AppShellBar (one touch-target row plus its padding), clear of the
 * bottom-right PwaUpdateBanner and the assistant.
 */
export function DashboardPushToast() {
  const { t } = useI18n();
  const { latestMessage, dismissLatest } = usePushRegistration();
  const follow = useFollowPushLink();
  return (
    <PushToast
      message={latestMessage}
      fallbackTitle={t('common.push.toastFallbackTitle')}
      openLabel={t('common.push.toastOpen')}
      dismissLabel={t('common.push.toastDismiss')}
      onOpen={follow}
      onDismiss={dismissLatest}
      top={TOAST_TOP}
    />
  );
}
